// https://github.com/orestbida/cookieconsent/tree/v2.9
import { merge } from 'lodash-es';
import prestashop from 'prestashop';
import { cookies, TCookies } from './categories';

type TCallbacks = {
    // Any consent state (first choice, change, every page load once given)
    onConsent: () => void;
    // First choice of the visitor: refused categories
    onFirstChoice: (rejectedCategories: string[]) => void;
    // Choice changed: categories that were accepted and are now refused
    onWithdraw: (withdrawnCategories: string[]) => void;
};

// Texts are injected by templates/_partials/i18n.tpl (Smarty translations)
const i18n = (prestashop.i18n || {}) as LanguageSetting;

const makeCookieBlock = (
    category: string,
    readonly = false,
    enabled = true,
) => ({
    toggle: {
        value: category,
        readonly,
        enabled,
    },
});

const makeCookieBlocks = (cookies: TCookies) =>
    Object.entries(cookies).map(([value, [readonly, enabled]]) =>
        makeCookieBlock(value, readonly, enabled),
    );

const makeLang = (iso_code: string) => ({
    [iso_code]: merge({}, i18n, {
        consent_modal: {
            primary_btn: {
                role: 'accept_all',
            },
            secondary_btn: {
                role: 'settings',
            },
        },
        settings_modal: {
            blocks: makeCookieBlocks(cookies),
        },
    }),
});

export const makeConfig = ({
    onConsent,
    onFirstChoice,
    onWithdraw,
}: TCallbacks): UserConfig => ({
    // Increment to ask everyone for consent again
    revision: 1,
    current_lang: 'fr',
    cookie_name: prestashop.cookieName as string,
    page_scripts: true,
    mode: 'opt-in',
    force_consent: true,
    cookie_expiration: 395,
    // Refusal is remembered 6 months before asking again
    cookie_necessary_only_expiration: 182,

    // Trackers are removed by cleanup.ts (patterns in categories.ts), not by the library
    autoclear_cookies: false,

    onAccept() {
        onConsent();
    },

    onFirstAction(preferences) {
        onFirstChoice(preferences.rejected_categories);
    },

    onChange(cookie, changedCategories) {
        onConsent();
        onWithdraw(
            changedCategories.filter(
                (category) => !cookie.categories.includes(category),
            ),
        );
    },

    gui_options: {
        consent_modal: {
            layout: 'cloud',
            transition: 'slide',
            swap_buttons: true,
        },
        settings_modal: {
            layout: 'bar',
            position: 'right',
            transition: 'slide',
        },
    },

    languages: {
        ...makeLang('fr'),
    },
});
